import axios from 'axios';
import { API } from './authApi';

/*
 * Subscriptions and the referral program — the thin client for backend/billing.py. Stripe itself is only ever
 * reached through redirects (Checkout, Customer Portal); the browser never holds a Stripe key.
 */

export const getBillingConfig = async () => (await axios.get(`${API}/billing/config`)).data; // { enabled, live, trial_days, plans[] }
export const getBillingMe = async () => (await axios.get(`${API}/billing/me`)).data; // { enabled, billing, allows, referral, trial_eligible }
/** Start Stripe Checkout for 'monthly' | 'annual' → { url, session_id, trial }. The browser is then sent to `url`. */
export const startCheckout = async ({ plan, referralCode }) => (await axios.post(`${API}/billing/checkout`, { plan, origin_url: window.location.origin, referral_code: referralCode || null })).data;
/** Back from Checkout: have the backend read the session and store the subscription → { complete, billing, allows }. */
export const completeCheckout = async (sessionId) => (await axios.post(`${API}/billing/checkout/complete`, { session_id: sessionId })).data;
/** Re-read the subscription from Stripe (after the portal, or when a webhook might have been missed) → { billing, allows }. */
export const syncBilling = async () => (await axios.post(`${API}/billing/sync`)).data;
/** Stripe's hosted Customer Portal (update card, switch plan, cancel, invoices) → { url }. */
export const openPortal = async () => (await axios.post(`${API}/billing/portal`, { origin_url: window.location.origin })).data;
export const getReferral = async () => (await axios.get(`${API}/billing/referral`)).data; // { code, link_path, friends[], rewards_count, rewards_cents, rules }
export const lookupReferral = async (code) => (await axios.get(`${API}/billing/referral/${encodeURIComponent(code)}`)).data; // public: { valid, referrer_first_name }
export const applyReferral = async (code) => (await axios.post(`${API}/billing/referral/apply`, { code })).data;

/* ------------------------------------------------------------------ */
/* Referral code from ?ref=… — kept until the account exists           */
/* ------------------------------------------------------------------ */

const REF_KEY = 'wp.referralCode'; // the public code from a shared invite link (not a secret), kept until sign-up or 30 days
const REF_DAYS = 30;

export const cleanReferralCode = (code) => String(code || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);

export const rememberReferralCode = (code) => {
  const c = cleanReferralCode(code);
  if (!c) return;
  try { window.localStorage.setItem(REF_KEY, JSON.stringify({ code: c, at: Date.now() })); } catch { /* storage unavailable */ }
};

export const pendingReferralCode = () => {
  try {
    const raw = window.localStorage.getItem(REF_KEY);
    if (!raw) return '';
    const { code, at } = JSON.parse(raw);
    if (!code || Date.now() - (at || 0) > REF_DAYS * 86400000) { window.localStorage.removeItem(REF_KEY); return ''; }
    return code;
  } catch { return ''; }
};

export const forgetReferralCode = () => { try { window.localStorage.removeItem(REF_KEY); } catch { /* ignore */ } };

/* ------------------------------------------------------------------ */
/* Where to go once the subscription is in place (survives Stripe)     */
/* ------------------------------------------------------------------ */

const NEXT_KEY = 'wp.afterSubscribe';
export const rememberAfterSubscribe = (path) => { try { if (path && path.startsWith('/app')) window.sessionStorage.setItem(NEXT_KEY, path); } catch { /* ignore */ } };
export const takeAfterSubscribe = () => {
  try {
    const p = window.sessionStorage.getItem(NEXT_KEY);
    window.sessionStorage.removeItem(NEXT_KEY);
    return p && p.startsWith('/app') ? p : '/app';
  } catch { return '/app'; }
};

/* ------------------------------------------------------------------ */
/* Display helpers                                                     */
/* ------------------------------------------------------------------ */

export const formatMoney = (cents, currency = 'usd') => {
  const whole = cents % 100 === 0;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
  } catch { return `$${(cents / 100).toFixed(whole ? 0 : 2)}`; }
};

export const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; }
};

export const daysUntil = (iso) => (iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000)) : 0);

export const PLAN_LABEL = { monthly: 'Monthly', annual: 'Annual', owner: 'Owner' };
export const PLAN_PRICE = { monthly: '$4.99 / month', annual: '$50 / year', owner: 'complimentary' };

/**
 * One line about the subscription for menus and badges.
 * → { tone: 'trial' | 'ok' | 'warn' | 'off' | 'none', title, detail }
 */
export const subscriptionSummary = (billing) => {
  const b = billing || {};
  const plan = PLAN_LABEL[b.plan] || 'WannaPublish';
  switch (b.status) {
    case 'trialing': {
      const n = daysUntil(b.trial_end);
      return { tone: 'trial', title: `Free trial · ${n} day${n === 1 ? '' : 's'} left`, detail: b.cancel_at_period_end ? `Ends ${formatDate(b.trial_end)} — your card will not be charged.` : `${plan} plan starts ${formatDate(b.trial_end)} (${PLAN_PRICE[b.plan] || ''}) unless you cancel first.` };
    }
    case 'active':
      if (b.plan === 'owner') return { tone: 'ok', title: 'Owner · complimentary', detail: 'Full access to everything, nothing is charged.' };
      return b.cancel_at_period_end
        ? { tone: 'warn', title: `${plan} plan · cancels ${formatDate(b.current_period_end)}`, detail: 'You keep full access until then. Resume any time from Billing & plan.' }
        : { tone: 'ok', title: `${plan} plan · ${PLAN_PRICE[b.plan] || ''}`, detail: `Renews ${formatDate(b.current_period_end)}.` };
    case 'past_due':
      return { tone: 'warn', title: 'Payment problem', detail: 'Stripe could not charge your card. Update it in Billing & plan to keep your access.' };
    case 'unpaid':
    case 'canceled':
    case 'incomplete_expired':
      return { tone: 'off', title: 'Subscription ended', detail: 'Pick a plan to continue where you left off.' };
    case 'incomplete':
      return { tone: 'warn', title: 'Payment not completed', detail: 'Your first payment did not go through.' };
    default:
      return { tone: 'none', title: 'No subscription', detail: 'Start a plan to use the editor.' };
  }
};
